/* eslint-disable react/jsx-props-no-spreading */
import { Backdrop } from '@mui/material';
import { Box } from '@mui/system';
import UPButton from 'components/button';
import { UPDropdown, UPDropdownItem } from 'components/dropdown';
import UPDatepicker from 'components/datepicker';
import ListboxWithCheckboxes from 'components/listbox-with-checkbox/ListBoxWithCheckbox';
import { toNumber } from 'lodash';
import dayjs from 'utils/dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  addMotiveExceptionAction,
  clearMotiveExceptionErrorsAction,
  updateMotiveExceptionAction,
} from 'store/actions/motive-exception-actions';
import {
  addMotiveExceptionErrorSelector,
  addMotiveExceptionSuccessSelector,
  updateMotiveExceptionErrorSelector,
  updateMotiveExceptionSuccessSelector,
} from 'store/selectors/motive-exception-selectors';
import { activeEmployeeDropdownSelector } from 'store/selectors/employee-selector';
import { locationsDropdownItemsSelector } from 'store/selectors/company-locations-selectors';
import { payrollSelector } from 'store/selectors/payroll-selectors';
import {
  GENERAL__CANCEL_BUTTON,
  GENERAL__SAVE_BUTTON,
  GENERAL__SELECT,
  MOTIVE_EXCEPTION__ADD_TITLE,
  MOTIVE_EXCEPTION__ATTEMPT,
  MOTIVE_EXCEPTION__CLEAR_END_DATE,
  MOTIVE_EXCEPTION__EDIT_TITLE,
  MOTIVE_EXCEPTION__EMPLOYEES,
  MOTIVE_EXCEPTION__END_DATE,
  MOTIVE_EXCEPTION__LOCATIONS,
  MOTIVE_EXCEPTION__MOTIVE,
  MOTIVE_EXCEPTION__START_DATE,
  MOTIVE_EXCEPTION__TYPE,
  MOTIVE_EXCEPTION__TYPE_EMPLOYEE,
  MOTIVE_EXCEPTION__TYPE_LOCATION,
  VALIDATION__IS_REQUIRED,
} from 'translations/constants';
import theme from 'theme';
import IMotiveException, { MotiveExceptionType } from 'types/company/payroll/IMotiveException';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/Close.svg';
import {
  ErrorText,
  FormRow,
  ModalCloseIcon,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalWrapper,
} from './styles';
import { MotiveExceptionModalProps } from './types';

const INSTROOM_MOTIVE_CODE = 5;
const ATTEMPT_OPTIONS: UPDropdownItem[] = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
];

interface FormValues {
  type: string;
  targetIds: Array<string | number>;
  contractMotiveId: string;
  attempt: string;
  startDate: Date;
  endDate: Date | null;
}

const MotiveExceptionModal = (props: MotiveExceptionModalProps): JSX.Element => {
  const { onClose, motiveExceptionToEdit } = props;
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();

  const employees = useAppSelector(activeEmployeeDropdownSelector);
  const locations = useAppSelector(locationsDropdownItemsSelector);
  const payroll = useAppSelector(payrollSelector);

  const addError = useAppSelector(addMotiveExceptionErrorSelector);
  const addSuccess = useAppSelector(addMotiveExceptionSuccessSelector);
  const updateError = useAppSelector(updateMotiveExceptionErrorSelector);
  const updateSuccess = useAppSelector(updateMotiveExceptionSuccessSelector);

  const isEdit = !!motiveExceptionToEdit;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: motiveExceptionToEdit
      ? {
          type: motiveExceptionToEdit.type.toString(),
          targetIds: motiveExceptionToEdit.targetIds,
          contractMotiveId: motiveExceptionToEdit.contractMotiveId.toString(),
          attempt: motiveExceptionToEdit.attempt ? motiveExceptionToEdit.attempt.toString() : '',
          startDate: new Date(motiveExceptionToEdit.startDate),
          endDate: motiveExceptionToEdit.endDate ? new Date(motiveExceptionToEdit.endDate) : null,
        }
      : {
          type: MotiveExceptionType.EMPLOYEE.toString(),
          targetIds: [],
          contractMotiveId: '',
          attempt: '',
          startDate: null,
          endDate: null,
        },
  });

  const watchType = watch('type');
  const watchContractMotiveId = watch('contractMotiveId');
  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');

  useEffect(() => {
    dispatch(clearMotiveExceptionErrorsAction());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit ? updateSuccess : addSuccess) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSuccess, updateSuccess]);

  const typeOptions: UPDropdownItem[] = useMemo(
    () => [
      { value: MotiveExceptionType.EMPLOYEE.toString(), label: t(MOTIVE_EXCEPTION__TYPE_EMPLOYEE) },
      { value: MotiveExceptionType.LOCATION.toString(), label: t(MOTIVE_EXCEPTION__TYPE_LOCATION) },
    ],
    [t],
  );

  const targetOptions = useMemo(
    () => (toNumber(watchType) === MotiveExceptionType.EMPLOYEE ? employees : locations),
    [employees, locations, watchType],
  );

  const motiveOptions: UPDropdownItem[] = useMemo(
    () =>
      (payroll?.contractMotives ?? [])
        .filter(cm => cm.isPossible)
        .map(cm => ({ value: cm.code.toString(), label: t(cm.nameConstant) })),
    [payroll, t],
  );

  const isInstroom = toNumber(watchContractMotiveId) === INSTROOM_MOTIVE_CODE;

  const submitError = isEdit ? updateError : addError;

  useEffect(() => {
    if (submitError) {
      setIsSubmitting(false);
    }
  }, [submitError]);

  const onSubmit = useCallback(
    (values: FormValues) => {
      setIsSubmitting(true);
      const motiveException: IMotiveException = {
        id: motiveExceptionToEdit?.id ?? 0,
        companyId: toNumber(companyId),
        type: toNumber(values.type),
        contractMotiveId: toNumber(values.contractMotiveId),
        attempt: isInstroom && values.attempt !== '' ? toNumber(values.attempt) : null,
        startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
        endDate: values.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : null,
        targetIds: values.targetIds.map(toNumber),
      };

      if (isEdit) {
        dispatch(updateMotiveExceptionAction({ companyId: toNumber(companyId), motiveException }));
      } else {
        dispatch(addMotiveExceptionAction({ companyId: toNumber(companyId), motiveException }));
      }
    },
    [companyId, dispatch, isEdit, isInstroom, motiveExceptionToEdit],
  );

  return (
    <Backdrop open sx={{ zIndex: '1200' }}>
      <ModalWrapper open onClose={onClose}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>{isEdit ? t(MOTIVE_EXCEPTION__EDIT_TITLE) : t(MOTIVE_EXCEPTION__ADD_TITLE)}</ModalTitle>
            <ModalCloseIcon onClick={onClose}>
              <CloseIcon fill={theme.palette.secondary.contrastText} />
            </ModalCloseIcon>
          </ModalHeader>

          <FormRow>
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <UPDropdown
                  items={typeOptions}
                  value={field.value}
                  onChange={event => {
                    field.onChange(event);
                    // targetIds holds employee ids for one type and location ids for the other, so
                    // a selection carried across a type switch would be saved against the wrong
                    // entity, with its chips silently gone from the list.
                    if (event?.target?.value !== field.value) {
                      setValue('targetIds', []);
                    }
                  }}
                  placeholder={t(GENERAL__SELECT)}
                  label={t(MOTIVE_EXCEPTION__TYPE)}
                  required
                />
              )}
            />
          </FormRow>

          <FormRow>
            <Controller
              name="targetIds"
              control={control}
              rules={{ validate: v => (Array.isArray(v) && v.length > 0) || 'required' }}
              render={({ field }) => (
                <ListboxWithCheckboxes
                  label={
                    toNumber(watchType) === MotiveExceptionType.EMPLOYEE
                      ? t(MOTIVE_EXCEPTION__EMPLOYEES)
                      : t(MOTIVE_EXCEPTION__LOCATIONS)
                  }
                  options={targetOptions}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  required
                  searchable
                  enableBulkActions
                  maxListHeight={200}
                  helperText={t(VALIDATION__IS_REQUIRED)}
                />
              )}
            />
          </FormRow>

          <FormRow sx={{ flexDirection: 'row', gap: '16px' }}>
            <Box sx={{ flex: 1 }}>
              <Controller
                name="contractMotiveId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPDropdown
                    items={motiveOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t(GENERAL__SELECT)}
                    label={t(MOTIVE_EXCEPTION__MOTIVE)}
                    error={!!errors.contractMotiveId}
                    helperText={errors.contractMotiveId && t(VALIDATION__IS_REQUIRED)}
                    required
                  />
                )}
              />
            </Box>
            {isInstroom && (
              <Box sx={{ width: '160px', flexShrink: 0 }}>
                <Controller
                  name="attempt"
                  control={control}
                  rules={{ required: isInstroom }}
                  render={({ field }) => (
                    <UPDropdown
                      items={ATTEMPT_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t(GENERAL__SELECT)}
                      label={t(MOTIVE_EXCEPTION__ATTEMPT)}
                      error={!!errors.attempt}
                      helperText={errors.attempt && t(VALIDATION__IS_REQUIRED)}
                      required
                    />
                  )}
                />
              </Box>
            )}
          </FormRow>

          <FormRow sx={{ flexDirection: 'row', gap: '16px', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Controller
                name="startDate"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPDatepicker
                    value={field.value}
                    onChange={date => {
                      field.onChange(date);
                      if (date && (!watchEndDate || watchEndDate < date)) {
                        setValue('endDate', date);
                      }
                    }}
                    label={t(MOTIVE_EXCEPTION__START_DATE)}
                    required
                    error={!!errors.startDate}
                    helperText={errors.startDate && t(VALIDATION__IS_REQUIRED)}
                    maxDate={watchEndDate ?? undefined}
                  />
                )}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <UPDatepicker
                    value={field.value}
                    onChange={field.onChange}
                    label={t(MOTIVE_EXCEPTION__END_DATE)}
                    minDate={watchStartDate ?? undefined}
                  />
                )}
              />
            </Box>
            {watchEndDate && (
              <Box sx={{ width: '160px', flexShrink: 0, marginTop: '18px' }}>
                <UPButton
                  text={t(MOTIVE_EXCEPTION__CLEAR_END_DATE)}
                  onClick={() => setValue('endDate', null)}
                  outlined
                />
              </Box>
            )}
          </FormRow>

          {submitError && (
            <FormRow>
              <ErrorText>{t(submitError)}</ErrorText>
            </FormRow>
          )}

          <ModalFooter>
            <UPButton text={t(GENERAL__SAVE_BUTTON)} onClick={handleSubmit(onSubmit)} disabled={isSubmitting} />
            <UPButton text={t(GENERAL__CANCEL_BUTTON)} onClick={onClose} outlined />
          </ModalFooter>
        </ModalContainer>
      </ModalWrapper>
    </Backdrop>
  );
};

export default MotiveExceptionModal;
